'use client';

import { c as _c } from "react/compiler-runtime";
import { isUrl, mapLinkHref } from '@shared/all/validators';
import formatRelativeTime from '@shared/utils/helpers/formatRelativeTimeShort';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertTriangle, Calendar, Check, Clock, ExternalLink, FileText, Globe, Loader2, MapPin, Shield, Upload as UploadIcon, User, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { InView } from 'react-intersection-observer';
import { getS3ObjectURL } from '../../utils/s3';
import { client, type RouterOutputs } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import DocumentItem from '../common/DocumentItem';
import Error from '../common/Error';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import VideoPlayer from '../UI/Video';
type Props = {
  closeHandler: () => void;
  factiiiId: number;
};
type PostFactiiiReturn = RouterOutputs['factiiis']['pendingPostFactiiis']['postFactiiis'][number];
const ModerationPanel = t0 => {
  const $ = _c(32);
  const {
    closeHandler,
    factiiiId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [actionLoading, setActionLoading] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== factiiiId) {
    t2 = ["pending-postfactiiis", factiiiId];
    t3 = t4 => {
      const {
        pageParam
      } = t4;
      return unwrapFromResult(client.factiiis.pendingPostFactiiis.query({
        factiiiId,
        cursor: pageParam
      }));
    };
    $[1] = factiiiId;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      initialPageParam: undefined as number | undefined,
      getNextPageParam: _temp,
      select: _temp3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: data_0,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t4);
  let t5;
  if ($[7] !== refetch) {
    t5 = async (postFactiiiId, factiiiId_0) => {
      setActionLoading(prev => ({
        ...prev,
        [postFactiiiId]: "approve"
      }));
      await client.factiiis.approvePostFactiii.mutate({
        postFactiiiId,
        factiiiId: factiiiId_0
      });
      refetch();
      setActionLoading(prev_0 => ({
        ...prev_0,
        [postFactiiiId]: null
      }));
    };
    $[7] = refetch;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleApprove = t5;
  let t6;
  if ($[9] !== refetch) {
    t6 = async (postFactiiiId_0, factiiiId_1) => {
      setActionLoading(prev_1 => ({
        ...prev_1,
        [postFactiiiId_0]: "reject"
      }));
      await client.factiiis.rejectPostFactiii.mutate({
        postFactiiiId: postFactiiiId_0,
        factiiiId: factiiiId_1
      });
      refetch();
      setActionLoading(prev_2 => ({
        ...prev_2,
        [postFactiiiId_0]: null
      }));
    };
    $[9] = refetch;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleReject = t6;
  const renderUploads = _temp5;
  const renderSpaceTimes = _temp6;
  const renderReferences = _temp9;
  const renderData = _temp10;
  const renderPostFactiiiReferences = _temp12;
  let t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex size-10 items-center justify-center rounded-full"><Shield className="size-5 text-primary" /></div>;
    t8 = <h2 className="text-xl font-semibold text-textPrimary">Moderation Queue</h2>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  const t9 = data_0?.postFactiiis?.length || 0;
  let t10;
  if ($[13] !== t9) {
    t10 = <div className="flex items-center gap-3">{t7}<div>{t8}<p className="text-sm text-textSecondary">{t9} items pending review</p></div></div>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== closeHandler || $[16] !== t10) {
    t11 = <ModalHeader closeHandler={closeHandler}>{t10}</ModalHeader>;
    $[15] = closeHandler;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== actionLoading || $[19] !== data_0 || $[20] !== error || $[21] !== fetchNextPage || $[22] !== handleApprove || $[23] !== handleReject || $[24] !== hasNextPage || $[25] !== isFetchingNextPage || $[26] !== isLoading) {
    t12 = <ModalContent>{isLoading ? <div className="flex flex-col items-center justify-center py-12"><div className="flex size-16 items-center justify-center rounded-full"><Clock className="size-8 animate-spin text-primary" /></div><h3 className="mt-4 text-lg font-emphasis text-textPrimary">Loading queue...</h3><p className="mt-2 text-sm text-textSecondary">Fetching pending moderation items</p></div> : <div className="space-y-3">{data_0?.postFactiiis && data_0.postFactiiis.length > 0 ? <><div className="mb-6 rounded-xl border border-ds-border-standard p-4"><div className="flex items-center justify-between"><div className="flex items-center gap-4"><div className="text-center"><div className="text-2xl font-semibold text-primary">{data_0.postFactiiis.length}</div><div className="text-xs font-emphasis text-textSecondary">Pending</div></div><div className="h-8 w-px bg-ds-border-standard" /><div className="text-center"><div className="text-2xl font-semibold text-alert"><AlertTriangle className="size-6" /></div><div className="text-xs font-emphasis text-textSecondary">Requires Action</div></div></div><div className="text-right"><div className="text-sm font-emphasis text-textPrimary">Moderation Queue</div><div className="text-xs text-textSecondary">Review and approve content</div></div></div></div>{data_0.postFactiiis.map(item => <div key={item.id} className="group rounded-xl border border-ds-border-standard bg-background-secondary p-5 shadow-sm transition-all duration-200 hover:shadow-md"><div className="mb-4 flex items-start justify-between"><div className="flex items-center gap-3"><div className="relative">{item.user.robohash && item.user.avatarKey && <Avatar avatarKey={item.user.avatarKey} robohash={item.user.robohash} isBot={item.user.tag === "BOT"} userId={item.user.id} size={48} />}<div className="absolute -bottom-1 -right-1 flex size-6 items-center justify-center rounded-full bg-alert"><Clock className="size-3 text-white" /></div></div><div><div className="flex items-center gap-2"><p className="font-semibold text-textPrimary">{item.user.name}</p><span className="rounded-full bg-alert/10 px-2 py-1 text-xs font-emphasis text-alert">Pending Review</span></div><div className="flex items-center gap-1 text-sm text-textSecondary"><User className="size-3" /><span>@{item.user.username}</span></div></div></div><div className="text-right"><div className="flex items-center gap-1 text-xs text-textSecondary"><Calendar className="size-3" /><span>{formatRelativeTime(item.createdAt)}</span></div></div></div><div className="mb-4"><div className="mb-2 flex items-center gap-2"><FileText className="size-4 text-textSecondary" /><span className="text-sm font-emphasis text-textPrimary">Post</span></div><Link href={`/post/${item.postId}`} className="inline-flex items-center gap-2 rounded-lg border border-ds-border-standard bg-background-primary p-3 transition-all" onClick={_temp13}><span className="text-sm font-emphasis text-textPrimary">Post ID: {item.postId}</span><ExternalLink className="size-4 text-textSecondary" /></Link></div><div className="mb-4"><div className="mb-3 flex items-center gap-2"><FileText className="size-4 text-primary" /><span className="text-sm font-emphasis text-textPrimary">PostFactiii Details</span>{item.postFactiiii.anonymous && <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-emphasis text-orange-600">Anonymous</span>}</div><div className="space-y-3 rounded-lg border border-ds-border-standard p-4">{renderData(item.postFactiiii.data)}{renderData(item.postFactiiii.number?.toString() ?? null)}{renderSpaceTimes(item.postFactiiii.spaceTimes, item.postId)}{renderUploads(item.postFactiiii.uploads)}{renderReferences(item.postFactiiii.references)}{renderPostFactiiiReferences(item.postFactiiii.postFactiiiReferences)}</div></div><div className="flex items-center gap-3 pt-2"><Button text={actionLoading[item.id] === "approve" ? "Approving..." : "Approve"} onClick={() => item.factiii?.id && handleApprove(item.id, item.factiii.id)} disabled={Boolean(actionLoading[item.id]) || !item.factiii?.id} variant="PRIMARY" icon={<Check className="size-4" />} classNames="flex-1" /><Button text={actionLoading[item.id] === "reject" ? "Rejecting..." : "Reject"} onClick={() => item.factiii?.id && handleReject(item.id, item.factiii.id)} disabled={Boolean(actionLoading[item.id]) || !item.factiii?.id} variant="DANGER" icon={<X className="size-4" />} classNames="flex-1" /></div></div>)}{hasNextPage && <InView onChange={isVisible => isVisible && !isFetchingNextPage ? fetchNextPage() : undefined} className="flex justify-center py-4"><Loader2 className="animate-spin" /></InView>}{!hasNextPage && data_0.postFactiiis.length > 0 && <div className="flex justify-center py-4"><p className="text-sm text-textSecondary">You've reached the end of the queue 🎉</p></div>}</> : <div className="flex flex-col items-center justify-center py-16"><div className="flex size-20 items-center justify-center rounded-full"><Check className="size-10 text-primary" /></div><h3 className="mt-6 text-lg font-semibold text-textPrimary">All clear!</h3><p className="mt-2 max-w-sm text-center text-sm text-textSecondary">No pending postFactiiis require moderation at this time. Check back later for new submissions.</p></div>}{error && <Error error={error} loading={isLoading} message="An error occurred while loading moderation items. Please refresh the page." />}</div>}</ModalContent>;
    $[18] = actionLoading;
    $[19] = data_0;
    $[20] = error;
    $[21] = fetchNextPage;
    $[22] = handleApprove;
    $[23] = handleReject;
    $[24] = hasNextPage;
    $[25] = isFetchingNextPage;
    $[26] = isLoading;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== closeHandler || $[29] !== t11 || $[30] !== t12) {
    t13 = <Modal closeHandler={closeHandler}>{t11}{t12}</Modal>;
    $[28] = closeHandler;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};
export default ModerationPanel;
function _temp(lastPage) {
  const postFactiiis = lastPage.postFactiiis;
  if (postFactiiis.length < 10) {
    return;
  }
  return postFactiiis[postFactiiis.length - 1]?.id;
}
function _temp2(page) {
  return page.postFactiiis;
}
function _temp3(data) {
  return {
    postFactiiis: data.pages.flatMap(_temp2)
  };
}
function _temp4(upload) {
  return <div key={upload.id} className="relative overflow-hidden rounded-lg">{upload.type === "IMAGE" && <Image src={getS3ObjectURL(upload.display.key)} alt={upload.name || "Upload"} width={120} height={80} className="h-20 w-full object-cover" />}{upload.type === "VIDEO" && <VideoPlayer src={getS3ObjectURL(upload.display.key)} className="h-20" controls={false} preload="metadata" />}{upload.type === "DOCUMENT" && <DocumentItem data={upload} />}</div>;
}
function _temp5(uploads) {
  if (!uploads || uploads.length === 0) {
    return null;
  }
  return <div className="mt-3"><div className="mb-2 flex items-center gap-2"><UploadIcon className="size-4 text-textSecondary" /><span className="text-sm font-emphasis text-textPrimary">Uploads ({uploads.length})</span></div><div className="grid grid-cols-2 gap-2 md:grid-cols-3">{uploads.map(_temp4)}</div></div>;
}
function _temp6(spaceTimes, postId) {
  if (!spaceTimes || spaceTimes.length === 0) {
    return null;
  }
  return <div className="mt-3"><div className="mb-2 flex items-center gap-2"><MapPin className="size-4 text-textSecondary" /><span className="text-sm font-emphasis text-textPrimary">{"Location & Time Data"}</span></div><div className="space-y-2">{spaceTimes.map(spaceTime => <div key={spaceTime.id} className="rounded-lg border border-ds-border-standard bg-background-primary p-3">{spaceTime.latitude && spaceTime.longitude && <div className="flex items-center gap-2"><MapPin className="size-4 text-primary" /><Link href={`/map?${mapLinkHref.parse({
            startPosition: {
              postId,
              latitude: spaceTime.latitude,
              longitude: spaceTime.longitude
            },
            pins: []
          }).href}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">{spaceTime.display || `${spaceTime.latitude}, ${spaceTime.longitude}`}</Link></div>}{spaceTime.timestamp && <div className="mt-1 flex items-center gap-2"><Clock className="size-4 text-textSecondary" /><span className="text-sm text-textPrimary">{new Date(spaceTime.timestamp).toLocaleString()}</span></div>}</div>)}</div></div>;
}
function _temp7(e) {
  return e.stopPropagation();
}
function _temp8(post) {
  return <div className="mb-4" key={post}><div className="mb-2 flex items-center gap-2"><FileText className="size-4 text-textSecondary" /><span className="text-sm font-emphasis text-textPrimary">Post</span></div><Link href={`/post/${post}`} className="inline-flex items-center gap-2 rounded-lg border border-ds-border-standard bg-background-primary p-3 transition-all" onClick={_temp7}><span className="text-sm font-emphasis text-textPrimary">Post ID: {post}</span><ExternalLink className="size-4 text-textSecondary" /></Link></div>;
}
function _temp9(references) {
  if (!references || references.length === 0) {
    return null;
  }
  return <div className="mt-3"><div className="mb-2 flex items-center gap-2"><ExternalLink className="size-4 text-textSecondary" /><span className="text-sm font-emphasis text-textPrimary">Post References ({references.length})</span></div><div className="space-y-3">{references.map(_temp8)}</div></div>;
}
function _temp10(data_1) {
  if (!data_1) {
    return null;
  }
  return <div className="mt-3"><div className="mb-2 flex items-center gap-2"><FileText className="size-4 text-textSecondary" /><span className="text-sm font-emphasis text-textPrimary">Data</span></div><div className="rounded-lg border border-ds-border-standard bg-background-primary p-3">{isUrl(data_1) ? <Link href={data_1} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline"><Globe className="size-4" /><span className="break-all text-sm">{data_1}</span><ExternalLink className="size-4" /></Link> : <p className="break-words text-sm text-textPrimary">{data_1}</p>}</div></div>;
}
function _temp11(source) {
  return <div key={source.factiiiId} className="rounded-lg border border-ds-border-standard bg-background-primary p-3"><div className="mb-2 flex items-center gap-3">{source.factiii.avatarKey && <Avatar factiiiAvatarKey={source.factiii.avatarKey} factiiiId={source.factiii.id} size={32} />}<div className="flex-1"><Link href={`/fids/${source.factiii.id}`} className="font-emphasis text-textPrimary hover:text-primary">{source.factiii.name}</Link><p className="text-xs text-textSecondary">f/{source.factiii.slug}</p></div></div>{source.data && <div className="text-sm text-textSecondary">{isUrl(source.data) ? <Link href={source.data} target="_blank" rel="noopener noreferrer" className="break-all text-primary hover:underline">{source.data}</Link> : <p className="break-words">{source.data}</p>}</div>}</div>;
}
function _temp12(postFactiiiReferences) {
  if (!postFactiiiReferences || postFactiiiReferences.length === 0) {
    return null;
  }
  return <div className="mt-3"><div className="mb-2 flex items-center gap-2"><Shield className="size-4 text-textSecondary" /><span className="text-sm font-emphasis text-textPrimary">Source References ({postFactiiiReferences.length})</span></div><div className="space-y-3">{postFactiiiReferences.map(_temp11)}</div></div>;
}
function _temp13(e_0) {
  return e_0.stopPropagation();
}